import React, { useState, useEffect } from 'react';
import Calendar from 'react-calendar';
import { Link } from 'react-router-dom';
import { db } from '../../firebase/config';
import { doc, getDoc, setDoc, collection, getDocs, updateDoc, arrayUnion } from 'firebase/firestore';
import { Users, Settings, ShieldCheck, UserCog, Calendar as CalIcon, Briefcase, UserCheck, Clock, Save, Loader, FileText } from 'lucide-react';
import ContentTab from './ContentTab';

type UserRecord = {
  id: string;
  email?: string;
  fullName?: string;
  phoneNumber?: string;
  role?: string;
  status?: string;
  assignedLawyerId?: string | null;
  assignedClients?: string[];
  createdAt?: string;
  teamHeadline?: string;
  teamComments?: string;
  featuredInTeam?: boolean;
};

type LawyerProfileDraft = {
  teamHeadline: string;
  teamComments: string;
  featuredInTeam: boolean;
};

const AdminDashboard: React.FC = () => {
  const [isAdminOptionVisible, setIsAdminOptionVisible] = useState(false);
  const [isLawyerOptionVisible, setIsLawyerOptionVisible] = useState(true);
  const [users, setUsers] = useState<UserRecord[]>([]);
  const [lawyers, setLawyers] = useState<UserRecord[]>([]);
  const [clients, setClients] = useState<UserRecord[]>([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState<'users' | 'settings' | 'assignments' | 'schedules' | 'content'>('users');

  // Schedule management states
  const [selectedLawyerId, setSelectedLawyerId] = useState<string>('');
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [availableSlots, setAvailableSlots] = useState<string[]>([]);
  const [savingSchedule, setSavingSchedule] = useState(false);
  const [lawyerDrafts, setLawyerDrafts] = useState<Record<string, LawyerProfileDraft>>({});
  const [savingLawyerProfileId, setSavingLawyerProfileId] = useState<string | null>(null);

  const timeSlots = ['09:00 AM', '10:00 AM', '11:00 AM', '12:00 PM', '01:00 PM', '02:00 PM', '03:00 PM', '04:00 PM', '05:00 PM'];

  useEffect(() => {
    fetchData();
  }, []);

  useEffect(() => {
    if (activeTab === 'schedules' && selectedLawyerId) {
      fetchLawyerAvailability();
    }
  }, [selectedLawyerId, selectedDate, activeTab]);

  const fetchData = async () => {
    setLoading(true);
    // Auto-Initialize settings if they don't exist
    const settingsDocRef = doc(db, 'settings', 'global');
    const settingsDoc = await getDoc(settingsDocRef);
    if (!settingsDoc.exists()) {
      await setDoc(settingsDocRef, { isAdminOptionVisible: false, isLawyerOptionVisible: true });
      setIsAdminOptionVisible(false);
      setIsLawyerOptionVisible(true);
    } else {
      const data = settingsDoc.data();
      setIsAdminOptionVisible(data.isAdminOptionVisible ?? false);
      setIsLawyerOptionVisible(data.isLawyerOptionVisible ?? true);
    }

    // Fetch users
    const usersSnap = await getDocs(collection(db, 'users'));
    const usersList: UserRecord[] = usersSnap.docs.map((userDoc) => ({
      id: userDoc.id,
      ...userDoc.data(),
      status: userDoc.data().status || 'approved'
    }));
    setUsers(usersList);
    setLawyers(usersList.filter((user) => user.role === 'lawyer' && user.status === 'approved'));
    setClients(usersList.filter(u => u.role === 'client'));
    const drafts = usersList
      .filter((user) => user.role === 'lawyer')
      .reduce<Record<string, LawyerProfileDraft>>((acc, lawyer) => {
        acc[lawyer.id] = {
          teamHeadline: lawyer.teamHeadline || '',
          teamComments: lawyer.teamComments || '',
          featuredInTeam: !!lawyer.featuredInTeam,
        };
        return acc;
      }, {});
    setLawyerDrafts(drafts);
    setLoading(false);
  };

  const updateLawyerDraft = (lawyerId: string, nextDraft: Partial<LawyerProfileDraft>) => {
    setLawyerDrafts((prev) => {
      const current = prev[lawyerId] || {
        teamHeadline: '',
        teamComments: '',
        featuredInTeam: false,
      };

      return {
        ...prev,
        [lawyerId]: {
          ...current,
          ...nextDraft,
        },
      };
    });
  };

  const saveLawyerProfile = async (lawyerId: string) => {
    const draft = lawyerDrafts[lawyerId] || {
      teamHeadline: '',
      teamComments: '',
      featuredInTeam: false,
    };

    setSavingLawyerProfileId(lawyerId);
    try {
      await updateDoc(doc(db, 'users', lawyerId), {
        teamHeadline: draft.teamHeadline.trim().slice(0, 1000),
        teamComments: draft.teamComments.trim().slice(0, 3000),
        featuredInTeam: draft.featuredInTeam,
      });
      alert('Lawyer profile details saved.');
      fetchData();
    } finally {
      setSavingLawyerProfileId(null);
    }
  };

  const fetchLawyerAvailability = async () => {
    if (!selectedLawyerId) return;
    const dateStr = selectedDate.toISOString().split('T')[0];
    const availDoc = await getDoc(doc(db, 'availability', `${selectedLawyerId}_${dateStr}`));
    if (availDoc.exists()) {
      setAvailableSlots(availDoc.data().slots || []);
    } else {
      setAvailableSlots([]);
    }
  };

  const saveLawyerSchedule = async () => {
    if (!selectedLawyerId) return;
    setSavingSchedule(true);
    const dateStr = selectedDate.toISOString().split('T')[0];
    await setDoc(doc(db, 'availability', `${selectedLawyerId}_${dateStr}`), {
      lawyerId: selectedLawyerId,
      date: dateStr,
      slots: availableSlots
    });
    setSavingSchedule(false);
    alert('Lawyer schedule updated successfully!');
  };

  const toggleSlot = (slot: string) => {
    setAvailableSlots(prev => 
      prev.includes(slot) ? prev.filter(s => s !== slot) : [...prev, slot]
    );
  };

  const updateSetting = async (key: string, value: any) => {
    await setDoc(doc(db, 'settings', 'global'), { [key]: value }, { merge: true });
    if (key === 'isAdminOptionVisible') setIsAdminOptionVisible(value);
    if (key === 'isLawyerOptionVisible') setIsLawyerOptionVisible(value);
  };

  const assignClientToLawyer = async (clientId: string, lawyerId: string) => {
    if (!lawyerId) return;
    await updateDoc(doc(db, 'users', clientId), { assignedLawyerId: lawyerId });
    await updateDoc(doc(db, 'users', lawyerId), { assignedClients: arrayUnion(clientId) });
    alert('Client assigned successfully!');
    fetchData();
  };

  const updateUserStatus = async (userId: string, status: 'pending' | 'approved') => {
    await updateDoc(doc(db, 'users', userId), { status });
    fetchData();
  };

  const getStatusStyles = (status?: string) => {
    if ((status || 'approved') === 'pending') {
      return { backgroundColor: '#fff3e0', color: '#ef6c00' };
    }

    return { backgroundColor: '#e8f5e9', color: '#2e7d32' };
  };

  const pendingLawyers = users.filter((user) => user.role === 'lawyer' && (user.status || 'approved') === 'pending');
  const sortedUsers = [...users].sort((left, right) => {
    const leftPendingLawyer = left.role === 'lawyer' && (left.status || 'approved') === 'pending';
    const rightPendingLawyer = right.role === 'lawyer' && (right.status || 'approved') === 'pending';
    if (leftPendingLawyer && !rightPendingLawyer) return -1;
    if (!leftPendingLawyer && rightPendingLawyer) return 1;
    return (left.email || '').localeCompare(right.email || '');
  });

  if (loading) return <div style={{ padding: '40px', textAlign: 'center' }}>Loading Executive Panel...</div>;

  return (
    <div className="container" style={{ padding: '40px 0' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '30px' }}>
        <h1 style={{ color: '#0f172a' }}>Executive Admin Dashboard</h1>
        <div style={{ display: 'flex', gap: '10px' }}>
          <button onClick={() => setActiveTab('users')} className={`btn ${activeTab === 'users' ? 'btn-primary' : ''}`} style={{ border: '1px solid #ddd' }}>User Management</button>
          <button onClick={() => setActiveTab('assignments')} className={`btn ${activeTab === 'assignments' ? 'btn-primary' : ''}`} style={{ border: '1px solid #ddd' }}>Assignments</button>
          <button onClick={() => setActiveTab('schedules')} className={`btn ${activeTab === 'schedules' ? 'btn-primary' : ''}`} style={{ border: '1px solid #ddd' }}>Schedules</button>
          <button onClick={() => setActiveTab('content')} className={`btn ${activeTab === 'content' ? 'btn-primary' : ''}`} style={{ border: '1px solid #ddd', display: 'flex', alignItems: 'center', gap: '8px' }}><FileText size={16} /> Content</button>
          <button onClick={() => setActiveTab('settings')} className={`btn ${activeTab === 'settings' ? 'btn-primary' : ''}`} style={{ border: '1px solid #ddd' }}>Settings</button>
        </div>
      </div>
      
      {activeTab === 'content' && <ContentTab />}

      {activeTab === 'settings' && (
        <div style={{ backgroundColor: 'white', padding: '30px', borderRadius: '12px', boxShadow: 'var(--shadow)' }}>
          <h3 style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '25px' }}>
            <Settings size={24} /> Platform Configuration
          </h3>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
            <label htmlFor="admin-registration-toggle" style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '15px', border: '1px solid #eee', borderRadius: '8px', cursor: 'pointer' }}>
              <div>
                <div style={{ fontWeight: 'bold' }}>Show Admin Registration Option</div>
                <div style={{ fontSize: '0.8rem', color: '#666' }}>Unchecked by default. When checked, users can select "Admin" during registration and will get Admin access immediately.</div>
              </div>
              <input
                id="admin-registration-toggle"
                name="isAdminOptionVisible"
                type="checkbox"
                checked={isAdminOptionVisible}
                onChange={(e) => updateSetting('isAdminOptionVisible', e.target.checked)}
                style={{ width: '18px', height: '18px', accentColor: 'var(--secondary-color)' }}
              />
            </label>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '15px', border: '1px solid #eee', borderRadius: '8px' }}>
              <div>
                <div style={{ fontWeight: 'bold' }}>Public Lawyer Registration</div>
                <div style={{ fontSize: '0.8rem', color: '#666' }}>Allow users to see "Lawyer" as an option on the Sign Up page.</div>
              </div>
              <ToggleButton active={isLawyerOptionVisible} onClick={() => updateSetting('isLawyerOptionVisible', !isLawyerOptionVisible)} />
            </div>
          </div>
        </div>
      )}

      {activeTab === 'users' && (
        <div style={{ backgroundColor: 'white', padding: '30px', borderRadius: '12px', boxShadow: 'var(--shadow)' }}>
          <h3 style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '25px' }}>
            <UserCog size={24} /> User Management
          </h3>

          <div style={{ marginBottom: '20px', padding: '14px 16px', borderRadius: '10px', border: '1px solid #ffe0b2', backgroundColor: '#fffaf2' }}>
            <div style={{ fontWeight: 700, color: '#bf360c', marginBottom: pendingLawyers.length > 0 ? '8px' : 0 }}>
              Pending Lawyer Approvals: {pendingLawyers.length}
            </div>
            {pendingLawyers.length > 0 && (
              <div style={{ fontSize: '0.9rem', color: '#7a4c00', display: 'grid', gap: '4px' }}>
                {pendingLawyers.map((pendingLawyer) => (
                  <span key={pendingLawyer.id}>{pendingLawyer.email} ({pendingLawyer.id})</span>
                ))}
              </div>
            )}
          </div>

          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr style={{ textAlign: 'left', borderBottom: '2px solid #eee' }}>
                <th style={{ padding: '15px' }}>Identity</th>
                <th style={{ padding: '15px' }}>Access Role</th>
                <th style={{ padding: '15px' }}>Approval Status</th>
                <th style={{ padding: '15px' }}>Action</th>
              </tr>
            </thead>
            <tbody>
              {sortedUsers.map(user => (
                <tr key={user.id} style={{ borderBottom: '1px solid #f5f5f5' }}>
                  <td style={{ padding: '15px' }}>
                    <div style={{ fontWeight: '600' }}>{user.email}</div>
                    <div style={{ fontSize: '0.75rem', color: '#999' }}>ID: {user.id}</div>
                  </td>
                  <td style={{ padding: '15px' }}>
                    <span style={{ 
                      padding: '4px 12px', 
                      borderRadius: '20px', 
                      fontSize: '0.75rem', 
                      fontWeight: 'bold',
                      backgroundColor: user.role === 'admin' ? '#ffebee' : user.role === 'lawyer' ? '#e8f5e9' : '#e3f2fd',
                      color: user.role === 'admin' ? '#c62828' : user.role === 'lawyer' ? '#2e7d32' : '#1565c0'
                    }}>
                      {user.role?.toUpperCase()}
                    </span>
                  </td>
                  <td style={{ padding: '15px' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '5px', color: '#2e7d32', fontSize: '0.85rem' }}>
                      <span style={{
                        padding: '4px 12px',
                        borderRadius: '20px',
                        fontSize: '0.75rem',
                        fontWeight: 'bold',
                        ...getStatusStyles(user.status)
                      }}>
                        {(user.status || 'approved').toUpperCase()}
                      </span>
                    </div>
                  </td>
                  <td style={{ padding: '15px' }}>
                    <div style={{ display: 'flex', gap: '8px', alignItems: 'center', flexWrap: 'wrap' }}>
                      <Link to={`/profile/${user.id}`} className="btn" style={{ padding: '8px 14px', fontSize: '0.8rem', backgroundColor: '#f5f5f5' }}>
                        View Profile
                      </Link>
                      {user.role === 'lawyer' ? (
                        (user.status || 'approved') === 'approved' ? (
                          <button
                            onClick={() => updateUserStatus(user.id, 'pending')}
                            className="btn"
                            style={{ padding: '8px 14px', fontSize: '0.8rem', backgroundColor: '#fff3e0', color: '#ef6c00' }}
                          >
                            Unapprove
                          </button>
                        ) : (
                          <button
                            onClick={() => updateUserStatus(user.id, 'approved')}
                            className="btn btn-secondary"
                            style={{ padding: '8px 14px', fontSize: '0.8rem' }}
                          >
                            Approve
                          </button>
                        )
                      ) : (
                        <span style={{ color: '#999', fontSize: '0.85rem' }}>No action needed</span>
                      )}

                      {user.role === 'lawyer' && (
                        <div style={{ marginTop: '10px', width: '100%', display: 'grid', gap: '8px' }}>
                          <label htmlFor={`lawyer-headline-${user.id}`} style={{ fontSize: '0.8rem', fontWeight: 600 }}>
                            Headline (max 1000)
                            <textarea
                              id={`lawyer-headline-${user.id}`}
                              name={`lawyerHeadline-${user.id}`}
                              value={lawyerDrafts[user.id]?.teamHeadline || ''}
                              maxLength={1000}
                              onChange={(event) => updateLawyerDraft(user.id, { teamHeadline: event.target.value })}
                              rows={3}
                              style={{ width: '100%', marginTop: '6px', border: '1px solid #ddd', borderRadius: '8px', padding: '10px', resize: 'vertical' }}
                            />
                            <div style={{ fontSize: '0.75rem', color: '#777' }}>{(lawyerDrafts[user.id]?.teamHeadline || '').length}/1000</div>
                          </label>

                          <label htmlFor={`lawyer-comments-${user.id}`} style={{ fontSize: '0.8rem', fontWeight: 600 }}>
                            Comments (max 3000)
                            <textarea
                              id={`lawyer-comments-${user.id}`}
                              name={`lawyerComments-${user.id}`}
                              value={lawyerDrafts[user.id]?.teamComments || ''}
                              maxLength={3000}
                              onChange={(event) => updateLawyerDraft(user.id, { teamComments: event.target.value })}
                              rows={5}
                              style={{ width: '100%', marginTop: '6px', border: '1px solid #ddd', borderRadius: '8px', padding: '10px', resize: 'vertical' }}
                            />
                            <div style={{ fontSize: '0.75rem', color: '#777' }}>{(lawyerDrafts[user.id]?.teamComments || '').length}/3000</div>
                          </label>

                          <label htmlFor={`lawyer-featured-${user.id}`} style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '0.85rem' }}>
                            <input
                              id={`lawyer-featured-${user.id}`}
                              name={`lawyerFeatured-${user.id}`}
                              type="checkbox"
                              checked={!!lawyerDrafts[user.id]?.featuredInTeam}
                              onChange={(event) => updateLawyerDraft(user.id, { featuredInTeam: event.target.checked })}
                              style={{ width: '16px', height: '16px', accentColor: 'var(--secondary-color)' }}
                            />
                            Feature this lawyer in About page Our Team section
                          </label>

                          <button
                            onClick={() => saveLawyerProfile(user.id)}
                            className="btn btn-primary"
                            disabled={savingLawyerProfileId === user.id}
                            style={{ width: 'fit-content', padding: '8px 14px', fontSize: '0.8rem' }}
                          >
                            {savingLawyerProfileId === user.id ? 'Saving...' : 'Save Lawyer Details'}
                          </button>
                        </div>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {activeTab === 'assignments' && (
        <div style={{ backgroundColor: 'white', padding: '30px', borderRadius: '12px', boxShadow: 'var(--shadow)' }}>
          <h3 style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '25px' }}>
            <Briefcase size={24} /> Client-Lawyer Pairings
          </h3>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))', gap: '20px' }}>
            {clients.map(client => (
              <div key={client.id} style={{ border: '1px solid #eee', borderRadius: '8px', padding: '20px' }}>
                <div style={{ marginBottom: '15px' }}>
                  <div style={{ fontWeight: 'bold' }}>{client.email}</div>
                  <div style={{ fontSize: '0.8rem', color: client.assignedLawyerId ? '#2e7d32' : '#c62828' }}>
                    {client.assignedLawyerId ? 'Assigned' : 'Unassigned'}
                  </div>
                </div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                  <label htmlFor={`client-assignment-${client.id}`} style={{ fontSize: '0.8rem', fontWeight: 'bold' }}>Assign to Lawyer:</label>
                  <select 
                    id={`client-assignment-${client.id}`}
                    name={`assignedLawyer-${client.id}`}
                    defaultValue={client.assignedLawyerId || ""}
                    onChange={(e) => assignClientToLawyer(client.id, e.target.value)}
                    style={{ padding: '10px', borderRadius: '4px', border: '1px solid #ddd' }}
                  >
                    <option value="" disabled>Select a Lawyer</option>
                    {lawyers.map(l => (
                      <option key={l.id} value={l.id}>{l.email}</option>
                    ))}
                  </select>
                  <Link to={`/profile/${client.id}`} className="btn" style={{ backgroundColor: '#f5f5f5', textAlign: 'center' }}>
                    View Profile
                  </Link>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {activeTab === 'schedules' && (
        <div style={{ backgroundColor: 'white', padding: '30px', borderRadius: '12px', boxShadow: 'var(--shadow)' }}>
          <h3 style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '25px' }}>
            <CalIcon size={24} /> Lawyer Schedule Management
          </h3>
          
          <div style={{ marginBottom: '30px', maxWidth: '400px' }}>
            <label htmlFor="schedule-lawyer-select" style={{ display: 'block', marginBottom: '10px', fontWeight: 'bold' }}>Select Lawyer:</label>
            <select 
              id="schedule-lawyer-select"
              name="selectedLawyerId"
              value={selectedLawyerId}
              onChange={(e) => setSelectedLawyerId(e.target.value)}
              style={{ width: '100%', padding: '12px', borderRadius: '8px', border: '1px solid #ddd' }}
            >
              <option value="">Select a lawyer to manage schedule...</option>
              {lawyers.map(l => (
                <option key={l.id} value={l.id}>{l.email}</option>
              ))}
            </select>
          </div>

          {selectedLawyerId ? (
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '40px', paddingTop: '20px', borderTop: '1px solid #eee' }}>
              <div>
                <h4 style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '15px' }}>
                  <CalIcon size={18} /> Choose Date
                </h4>
                <Calendar onChange={(v: any) => setSelectedDate(v)} value={selectedDate} className="admin-cal" />
              </div>
              <div>
                <h4 style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '15px' }}>
                  <Clock size={18} /> Manage Slots for {selectedDate.toDateString()}
                </h4>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '10px', marginBottom: '25px' }}>
                  {timeSlots.map(slot => (
                    <button
                      key={slot}
                      onClick={() => toggleSlot(slot)}
                      style={{
                        padding: '10px',
                        borderRadius: '6px',
                        border: `2px solid ${availableSlots.includes(slot) ? 'var(--secondary-color)' : '#eee'}`,
                        backgroundColor: availableSlots.includes(slot) ? '#fff9eb' : 'white',
                        color: availableSlots.includes(slot) ? 'var(--secondary-color)' : '#333',
                        fontWeight: availableSlots.includes(slot) ? 'bold' : 'normal',
                        fontSize: '0.8rem',
                        transition: 'var(--transition)'
                      }}
                    >
                      {slot}
                    </button>
                  ))}
                </div>
                <button 
                  onClick={saveLawyerSchedule}
                  disabled={savingSchedule}
                  className="btn btn-primary"
                  style={{ width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '10px' }}
                >
                  {savingSchedule ? <Loader className="spin" size={18} /> : <Save size={18} />}
                  {savingSchedule ? 'Saving...' : 'Confirm Lawyer Availability'}
                </button>
              </div>
            </div>
          ) : (
            <div style={{ textAlign: 'center', padding: '60px', backgroundColor: '#f9f9f9', borderRadius: '12px', border: '2px dashed #eee' }}>
              <Users size={48} style={{ color: '#ccc', marginBottom: '15px' }} />
              <p style={{ color: '#999' }}>Pick a lawyer from the dropdown to start managing their service schedule.</p>
            </div>
          )}
        </div>
      )}

      <style>{`
        .admin-cal { width: 100% !important; border: none !important; }
        .react-calendar__tile--active { background: var(--primary-color) !important; border-radius: 4px; }
        .spin { animation: rotate 2s linear infinite; }
        @keyframes rotate { 100% { transform: rotate(360deg); } }
      `}</style>
    </div>
  );
};

const ToggleButton = ({ active, onClick }: { active: boolean, onClick: () => void }) => (
  <button 
    onClick={onClick}
    style={{
      width: '50px',
      height: '24px',
      borderRadius: '12px',
      backgroundColor: active ? 'var(--secondary-color)' : '#ccc',
      position: 'relative',
      transition: 'var(--transition)'
    }}
  >
    <div style={{
      width: '18px',
      height: '18px',
      borderRadius: '50%',
      backgroundColor: 'white',
      position: 'absolute',
      top: '3px',
      left: active ? '29px' : '3px',
      transition: 'var(--transition)'
    }} />
  </button>
);

export default AdminDashboard;
